'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers'

const NumberLine = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.NumberLine), { ssr: false })
const HopLine = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.HopLine), { ssr: false })
const SquareVsAbs = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.SquareVsAbs), { ssr: false })

export default function NumberLineLesson() {
  return (
    <Lesson>
      <Step>
        <H>Numbers are places</H>
        <P>
          Forget sums for a moment. Picture an endless straight road with a signpost marked <strong>0</strong>. Every number is
          a spot on that road: <Hl c="blue">positive numbers</Hl> are to the right of 0, <Hl c="pink">negative numbers</Hl> to
          the left. This picture is called the <strong>number line</strong>, and it will quietly sit underneath every chart in
          this course.
        </P>
        <Figure n="2.1" caption="Drag the point. Decimals live between the whole numbers; negatives are mirror images of positives.">
          <NumberLine />
        </Figure>
        <P>
          Negative numbers aren’t exotic — they’re temperatures below freezing, money you owe, or a prediction that came out{' '}
          <em>too low</em>. And decimals like <C>2.5</C> simply sit between their neighbours.
        </P>
      </Step>

      <Step gate="q-add">
        <H>Adding and subtracting are moves</H>
        <P>
          On the number line, <strong>adding means moving right</strong> and <strong>subtracting means moving left</strong>.
          Start at <M>{'-3'}</M>, add 5: five steps right puts you at 2. That’s all <M>{'-3 + 5 = 2'}</M> means.
        </P>
        <Figure n="2.2" caption="Pick a starting point a and a move b. Multiplication stretches the arrow from 0 — and a negative number flips it to the other side.">
          <HopLine />
        </Figure>
        <P>
          <strong>Multiplying</strong> is stretching. <M>{'3 \\times 2'}</M> stretches the arrow from 0 to 3 until it’s twice as
          long. A negative multiplier also <em>flips</em> it to the other side — so multiplying two negatives flips twice and
          lands back on the positive side: <M>{'(-2) \\times (-3) = 6'}</M>.
        </P>
        <Exercise id="negatives" title="Check it in code" {...EX.negatives} />
        <Quiz
          id="q-add"
          question={<M>{'-7 + 4 = \\;?'}</M>}
          options={[{ label: '−3', correct: true }, { label: '3', why: 'You start at −7, left of zero. Four steps right gets you to −3 — still left of zero.' }, { label: '−11', why: 'That’s −7 − 4. Adding moves right, toward zero here.' }, { label: '11', why: 'Check the signs: −7 is the start, not 7.' }]}
        />
      </Step>

      <Step gate={['goal-abs', 'nq-dist']}>
        <H>Distance: how far, ignoring which way</H>
        <P>
          Often we care how <em>far</em> something is, not which direction. The <strong>absolute value</strong>, written with two
          bars <M>{'|x|'}</M>, is the distance from 0. So <M>{'|-4| = 4'}</M> and <M>{'|4| = 4'}</M>.
        </P>
        <P>
          And the distance between any two numbers <M>a</M> and <M>b</M> is <M>{'|a - b|'}</M>. A prediction of 210 when the
          truth was 200 is off by <M>{'|210 - 200| = 10'}</M>.
        </P>
        <Goal id="goal-abs" task={<>Drag the point to −6.5 — a distance of 6.5 from zero, on the negative side.</>}>
          {onReach => <NumberLine goal={-6.5} onGoal={onReach} />}
        </Goal>
        <NumberQuiz id="nq-dist" question={<>What is the distance between −4 and 3?</>} answer={7} explain={<>|−4 − 3| = |−7| = 7. Count it on the line: 4 steps to reach 0, then 3 more.</>} hint={<>Compute −4 − 3, then drop the minus sign.</>} />
      </Step>

      <Step gate="q-square">
        <H>Squaring: the number times itself</H>
        <P>
          To <strong>square</strong> a number, multiply it by itself: <M>{'x^2 = x \\times x'}</M>. The little raised 2 is read
          “squared” — because <M>{'x^2'}</M> is exactly the area of a square whose sides are <M>x</M> long.
        </P>
        <P>Two facts about squares make them the favourite tool of machine learning:</P>
        <ul className="gd-list">
          <li>
            <strong>A square is never negative.</strong> <M>{'(-3)^2 = 9'}</M> and <M>{'3^2 = 9'}</M> — two flips cancel out. An
            error of −3 and an error of +3 both count as 9.
          </li>
          <li>
            <strong>Big gets much bigger.</strong> An error of 1 squares to 1. An error of 10 squares to 100. Squaring punishes big
            mistakes far more than small ones.
          </li>
        </ul>
        <Figure n="2.3" caption={<>Drag the error. Absolute value (<Hl c="blue">blue</Hl>) grows steadily; the square (<Hl c="pink">pink</Hl>) stays tiny for small errors and explodes for big ones.</>}>
          <SquareVsAbs />
        </Figure>
        <Quiz
          id="q-square"
          question={<>What is <M>{'(-3)^2'}</M>?</>}
          options={[{ label: '−9', why: 'Squaring multiplies −3 by −3. Two negatives make a positive.' }, { label: '9', correct: true }, { label: '−6', why: 'That’s −3 × 2. Squaring is −3 × −3.' }, { label: '6', why: 'Squaring isn’t doubling — it’s multiplying the number by itself.' }]}
          explain="(−3) × (−3) = 9. Watch the brackets: −3² (without them) usually means −(3²) = −9, which trips people up constantly."
        />
      </Step>

      <Step gate="errors">
        <H>Your first error measurement</H>
        <P>
          Let’s use all of this. A model guessed some house prices; you’ll measure how wrong each guess was, then square the
          errors so that misses in either direction count as positive.
        </P>
        <MB>{'\\pink{\\text{error}} = \\amber{\\text{actual}} - \\text{predicted}'}</MB>
        <Exercise
          id="errors"
          title="Errors and squared errors"
          {...EX.errors}
          hints={[
            <>
              <C>{'p.map((pi, i) => a[i] - pi)'}</C> walks both arrays together using the index <C>i</C>.
            </>,
            <>
              For the squared version, square each error: <C>{'errors(p, a).map(e => e * e)'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          Later, “add up the squared errors and take the average” becomes the <strong>mean squared error</strong> — the loss
          function used to train countless models. You’ll write it yourself at the end of this module.
        </Callout>
      </Step>
    </Lesson>
  )
}
